import { supabase } from './supabase.js';

export class OrderCommunicationManager {
  constructor() {
    this.supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  }

  /**
   * Manually trigger communication for an order status change
   * Useful for testing or manual triggers from the dashboard
   */
  async triggerCommunication(orderId, newStatus, oldStatus = null) {
    try {
      // Get the order details
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .single();

      if (orderError) throw orderError;

      // Call the Edge Function directly
      const response = await fetch(`${this.supabaseUrl}/functions/v1/handle-order-status-change`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          record: { ...order, status: newStatus },
          old_record: { ...order, status: oldStatus }
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to trigger communication');
      }

      return result;
    } catch (error) {
      console.error('Error triggering communication:', error);
      throw error;
    }
  }

  /**
   * Get communication history for an order
   */
  async getOrderCommunications(orderId) {
    try {
      const { data, error } = await supabase
        .from('communication_history')
        .select('*')
        .eq('order_id', orderId)
        .order('sent_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching order communications:', error);
      throw error;
    }
  }

  /**
   * Get all communication templates
   */
  async getCommunicationTemplates() {
    try {
      const { data, error } = await supabase
        .from('communication_templates')
        .select('*')
        .order('status_trigger', { ascending: true })
        .order('type', { ascending: true });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching communication templates:', error);
      throw error;
    }
  }

  /**
   * Update order status and trigger communications automatically
   */
  async updateOrderStatus(orderId, newStatus) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;

      // The database trigger will automatically handle communication
      return data;
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  /**
   * Generate a new pickup or delivery code
   */
  generateSecureCode() {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  /**
   * Update order with pickup code
   */
  async setPickupCode(orderId, code = null) {
    const pickupCode = code || this.generateSecureCode();
    
    try {
      const { data, error } = await supabase
        .from('orders')
        .update({ pickup_code: pickupCode })
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error setting pickup code:', error);
      throw error;
    }
  }

  /**
   * Update order with delivery code
   */
  async setDeliveryCode(orderId, code = null) {
    const deliveryCode = code || this.generateSecureCode();
    
    try {
      const { data, error } = await supabase
        .from('orders')
        .update({ delivery_code: deliveryCode })
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error setting delivery code:', error);
      throw error;
    }
  }

  /**
   * Get order status flow with communication steps
   */
  getOrderStatusFlow() {
    return [
      {
        step: 1,
        title: 'Confirmation Sent',
        status: 'payment_succeeded',
        description: 'Customer receives pickup confirmation via SMS and email with all order details',
        timeline: 'Within 2 minutes',
        communicationType: ['sms', 'email']
      },
      {
        step: 2,
        title: 'Driver Dispatch',
        status: 'driver_dispatched_pickup',
        description: 'Driver dispatched for pickup, customer gets notification with pickup time and secure code',
        timeline: 'Day of pickup',
        communicationType: ['sms', 'email'],
        requiresCode: 'pickup_code'
      },
      {
        step: 3,
        title: 'Secure Pickup',
        status: 'picked_up',
        description: 'Driver arrives and provides confirmation code. Customer verifies before handing over clothes',
        timeline: 'At pickup time',
        communicationType: ['sms'],
        securityNote: 'Always verify the confirmation code before handing over your clothes'
      },
      {
        step: 4,
        title: 'Ironing Complete',
        status: 'ironing_complete',
        description: 'Ironing finished, customer receives delivery timing via email and SMS',
        timeline: 'Next day (or 24hrs for express)',
        communicationType: ['sms', 'email']
      },
      {
        step: 5,
        title: 'Delivery Dispatch',
        status: 'driver_dispatched_delivery',
        description: 'Driver dispatched with freshly pressed clothes. Customer receives delivery code',
        timeline: 'Day of delivery',
        communicationType: ['sms', 'email'],
        requiresCode: 'delivery_code'
      },
      {
        step: 6,
        title: 'Thank You & Reward',
        status: 'completed',
        description: 'After successful delivery, customer enjoys 10% coupon code for next order',
        timeline: 'After delivery',
        communicationType: ['sms', 'email'],
        reward: 'THANKYOU10 - 10% off next order'
      }
    ];
  }
}